import { AfterViewInit, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { TranslationService } from 'src/app/core/translation/translation.service';
import { StringUtils } from 'src/app/shared/common/utility/string-utils';
import { ChoiceFeatureField } from '../feature-field';

@Component({
    templateUrl: './choice-field.component.html',
    selector: 'choice-field'
})
export class ChoiceFieldComponent implements OnInit, AfterViewInit {
    @Input()
    field: ChoiceFeatureField;

    @Input()
    editMode: boolean;

    public _isOptionSelected: { [option: string]: boolean };
    public _isOtherOptionSelected: boolean;
    public _otherOption: string;
    private otherMark = '';
    public panelFooterHeight = 75;

    public showOtherForm = false;
    public selectedOptions: string[] = [];
    public selectedOthers: string[] = [];
    public noSelectionMark = '---';

    constructor(private cdRef: ChangeDetectorRef, private translate: TranslationService) {}

    ngOnInit(): void {
        const domainValues = this.field.value.filter(value => this.field.options.includes(value));
        const nonDomainValues = this.field.value.filter(value => !this.field.options.includes(value) && value !== '');
        const valueIncludesNonDomainValues = this.field.value.some(
            value => !this.field.options.includes(value) && value !== ''
        );

        this._isOptionSelected = {};
        this.selectedOptions = domainValues;
        this.selectedOthers = nonDomainValues;
        this._isOtherOptionSelected = valueIncludesNonDomainValues;
        this._otherOption = this.stringifiedOtherOption();
        this.showOtherForm = this.field.allowNonDomainValues;

        this.otherMark = `<${this.translate.instant('TCW_OTHER')}>`;

        this.setFromFieldValue();
    }

    ngAfterViewInit(): void {
        this.cdRef.detectChanges();
    }

    private setFromFieldValue(): void {
        if (this.showOtherForm) {
            if (!this.field.options.includes(this.otherMark)) {
                this.field.options.push(this.otherMark);
            } else if (this._isOtherOptionSelected && !this.selectedOptions.includes(this.otherMark)) {
                this.selectedOptions.push(this.otherMark);
            }
        }

        this.field.options.forEach(option => {
            this._isOptionSelected[option] = this.field.value.includes(option);
        });
    }

    private setToFieldValue(): void {
        let selected = Object.keys(this._isOptionSelected).filter(
            option => this._isOptionSelected[option] && option !== this.otherMark
        );
        if (this._isOtherOptionSelected) {
            selected = selected.concat(this._otherOption.split('|'));
        }
        this.field.value = selected;
    }

    public isSelected(option: string): boolean {
        if (option === this.otherMark) return false;

        return this._isOptionSelected[option];
    }

    public get isOtherOptionSelected(): boolean {
        return this._isOtherOptionSelected;
    }

    public toggleOtherOptionSelected(): void {
        this.clearAllOptionsIfAllowMultiSelect();
        this._isOtherOptionSelected = !this._isOtherOptionSelected;
        this.setToFieldValue();
    }

    private clearAllOptionsIfAllowMultiSelect(): void {
        if (!this.field.allowMultiSelect) {
            Object.keys(this._isOptionSelected).forEach(option2 => {
                this._isOptionSelected[option2] = false;
            });
            this._isOtherOptionSelected = false;
        }
    }

    public get otherOption(): string {
        if (this._isOtherOptionSelected) {
            // if unselected "other" option but "other field" has a value, select it as "Other" automatically.
            const selectOther = !this.selectedOptions?.includes(this.otherMark)
                ? this.selectedOptions.push(this.otherMark)
                : null;

            return this._otherOption;
        } else {
            return null;
        }
    }

    public set otherOption(option: string) {
        option = StringUtils.cleanupWhitespace(option);
        this._otherOption = option;
        this.setToFieldValue();

        this.cdRef.detectChanges();
    }

    public selectionChange(selectedOptions: string | string[]): void {
        // While using single selection the Dropdown component sends string output
        if (typeof selectedOptions === 'string') {
            selectedOptions = [selectedOptions];
        }
        this.field.options.forEach(option => {
            this._isOptionSelected[option] = selectedOptions?.includes(option);
        });

        this._isOtherOptionSelected =
            selectedOptions?.some(opt => opt === this.otherMark && this.field.allowNonDomainValues) || false;

        if (this._isOtherOptionSelected) {
            this._otherOption = this.stringifiedOtherOption();
        }

        this.showOtherForm = this._isOtherOptionSelected;
        this.setToFieldValue();
        this.cdRef.detectChanges();
    }

    private stringifiedOtherOption(): string {
        return this.selectedOthers.join('|').replace(/\|$/, '');
    }
}
